import React, { useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Upload, Loader2, X, Images } from "lucide-react";
import { toast } from "sonner";
import CropModal from "@/components/CropModal";
import StockPhotoPicker from "@/components/StockPhotoPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Molti loghi arrivano su una tela quadrata con parecchio margine bianco sopra e
// sotto al marchio vero e proprio (es. un wordmark largo centrato in un PNG 1:1).
// Usare l'aspect ratio del file grezzo non basta: qui misuriamo il riquadro del
// contenuto effettivo (pixel diversi dal colore di sfondo agli angoli) e ricaviamo
// da quello l'aspect ratio corretto, così il ritaglio parte già inquadrato sul logo
// invece di forzare un frame quadrato che obbliga a tagliare i lati per ingrandirlo.
function detectContentAspect(img, threshold = 24) {
  const scale = Math.min(1, 300 / img.naturalWidth);
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  let data;
  try { data = ctx.getImageData(0, 0, w, h).data; } catch (e) { return img.naturalWidth / img.naturalHeight || 1; }
  const corners = [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]];
  let br = 0, bg = 0, bb = 0;
  corners.forEach(([x, y]) => {
    const i = (y * w + x) * 4;
    br += data[i]; bg += data[i + 1]; bb += data[i + 2];
  });
  br /= 4; bg /= 4; bb /= 4;
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (data[i + 3] < 8) continue;
      const dr = data[i] - br, dg = data[i + 1] - bg, db = data[i + 2] - bb;
      if (Math.sqrt(dr * dr + dg * dg + db * db) > threshold) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX <= minX || maxY <= minY) return img.naturalWidth / img.naturalHeight || 1;
  const bw = maxX - minX + 1, bh = maxY - minY + 1;
  const padX = bw * 0.06, padY = bh * 0.06;
  return (bw + 2 * padX) / (bh + 2 * padY);
}

const ImageUpload = ({ value, onChange, label = "Immagine", testid = "image-upload", rounded = "rounded-xl", aspect = "aspect-video", presets = [], warnTransparency = false, freeAspect = false, allowBgRemoval = false, previewFit = "cover" }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropFormat, setCropFormat] = useState("image/jpeg");
  const [naturalAspect, setNaturalAspect] = useState(1);
  const [stockOpen, setStockOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const fixedAspect = aspect === "aspect-video" ? 16 / 9 : aspect === "aspect-[3/4]" ? 3 / 4 : 1;
  // Con freeAspect il riquadro di ritaglio riprende la forma del contenuto reale
  // del logo (non del file grezzo) invece di forzare un formato fisso che
  // finirebbe per tagliare via i bordi del logo.
  const cropAspect = freeAspect ? naturalAspect : fixedAspect;

  const warnOpaque = () => toast.warning("Il logo sembra avere uno sfondo pieno", { description: "Per un risultato più pulito nel menù, carica un PNG con sfondo trasparente oppure usa \"Rimuovi sfondo automaticamente\" nel ritaglio." });

  const checkOpaque = (url, type) => {
    if (type !== "image/png") { warnOpaque(); return; }
    const img = new Image();
    img.onload = () => {
      const w = Math.min(img.width, 96), h = Math.min(img.height, 96);
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      let transparent = false;
      for (let i = 3; i < data.length; i += 4) { if (data[i] < 250) { transparent = true; break; } }
      if (!transparent) warnOpaque();
    };
    img.src = url;
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCropFormat(f.type === "image/png" ? "image/png" : "image/jpeg");
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (warnTransparency) checkOpaque(dataUrl, f.type);
      if (freeAspect) {
        const img = new Image();
        img.onload = () => {
          setNaturalAspect(detectContentAspect(img));
          setCropSrc(dataUrl);
        };
        img.src = dataUrl;
      } else {
        setCropSrc(dataUrl);
      }
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const uploadBlob = async (blob) => {
    setUploading(true);
    try {
      const isPng = (blob.type || "") === "image/png";
      const fd = new FormData();
      fd.append("file", new File([blob], isPng ? "photo.png" : "photo.jpg", { type: isPng ? "image/png" : "image/jpeg" }));
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.path);
      toast.success("Immagine salvata");
    } catch (err) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</label>
      <div className={`relative ${aspect} w-full ${rounded} overflow-hidden border-2 border-dashed border-border bg-muted/40 flex items-center justify-center`} data-testid={testid}>
        {value ? (
          <>
            <img src={fileUrl(value)} alt={label} className={`w-full h-full object-${previewFit}`} />
            <button type="button" onClick={() => onChange(null)} className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5 hover:bg-black" data-testid={`${testid}-remove`}><X className="w-4 h-4" /></button>
          </>
        ) : uploading ? (
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()} className="flex flex-col items-center gap-1 text-muted-foreground">
            <Upload className="w-6 h-6" /><span className="text-sm">Carica foto</span>
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => inputRef.current?.click()} className="flex-1 min-w-[80px] rounded-lg border border-border py-2 text-xs font-medium hover:bg-muted flex items-center justify-center gap-1.5" data-testid={`${testid}-upload-btn`}><Upload className="w-3.5 h-3.5" /> Carica</button>
        {presets.length > 0 && (
          <button type="button" onClick={() => setPresetsOpen(true)} className="flex-1 min-w-[80px] rounded-lg border border-[#002FA7]/40 text-[#002FA7] py-2 text-xs font-medium hover:bg-[#002FA7]/5 flex items-center justify-center gap-1.5" data-testid={`${testid}-presets-btn`}><Images className="w-3.5 h-3.5" /> Sfondi</button>
        )}
        <button type="button" onClick={() => setStockOpen(true)} className="flex-1 min-w-[80px] rounded-lg border border-border py-2 text-xs font-medium hover:bg-muted flex items-center justify-center gap-1.5" data-testid={`${testid}-stock-btn`}><Images className="w-3.5 h-3.5" /> Libreria</button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testid}-input`} />
      {cropSrc && <CropModal open image={cropSrc} aspect={cropAspect} format={cropFormat} allowBgRemoval={allowBgRemoval} onCancel={() => setCropSrc(null)} onConfirm={async (blob) => { setCropSrc(null); await uploadBlob(blob); }} />}
      {stockOpen && <StockPhotoPicker open onClose={() => setStockOpen(false)} onSelect={(url) => { setStockOpen(false); onChange(url); toast.success("Foto libreria selezionata"); }} />}
      {presetsOpen && (
        <Dialog open onOpenChange={(o) => !o && setPresetsOpen(false)}>
          <DialogContent className="max-w-2xl" data-testid="bg-presets-dialog">
            <DialogHeader><DialogTitle>Sfondi eleganti</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto py-1">
              {presets.map((p) => (
                <button key={p.id} type="button" data-testid={`bg-preset-${p.id}`} onClick={() => { onChange(p.url); setPresetsOpen(false); toast.success("Sfondo selezionato"); }} className={`relative rounded-xl overflow-hidden border-2 aspect-[3/4] transition-colors ${value === p.url ? "border-[#002FA7]" : "border-transparent hover:border-[#002FA7]/50"}`}>
                  <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 inset-x-0 text-[10px] text-white bg-black/60 py-1 text-center">{p.label}</span>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ImageUpload;
